import { Children, isValidElement, useEffect, useState, type ReactElement, type ReactNode } from 'react';
import { fill, t, type StringKey } from '@bms2/shared';
import { SETUP_STEPS_KEY_PREFIX, readPreference, writePreference } from '../../shell/devicePreference';
import { Icon } from '../Icon';
import styles from './SetupSteps.module.css';

/**
 * Setup Steps (piece 4; decisions 1469, 1647, 1850, 2850): a numbered setup drawn as one
 * column of steps, each a card with its number in a badge, its title under
 * the word Step and its number, its state word, and then its body. Every
 * stepped setup screen in Settings draws through it, so a second screen is the
 * same card and not a copy of it.
 *
 * 🔒 A step is done, current or later, and the caller reads that from what is
 * saved; nothing here stores it. A done step draws as one compact row, its
 * check, title, summary and Done, and opens again to the full card on a press
 * of the row (decision 1850); a later step draws greyed
 * with its number plain, and the caller disables its controls. The notice
 * stands outside the body, so a done step still shows what it said.
 *
 * 🔒 A step held from going on says what holds it (decision 1970): the caller
 * passes the same sentence its disabled control carries, and the card draws it
 * under the state word, above the notice and the body, so nobody has to rest
 * the pointer on a greyed button to learn it. A done step draws none.
 */
export type SetupStepState = 'done' | 'current' | 'later';

export const setupStepsDocumentedStates = ['current', 'done', 'doneOpen', 'later', 'notice', 'folded'] as const;

const STATE_WORD: Readonly<Record<SetupStepState, StringKey>> = {
  done: 'setupSteps.done',
  current: 'setupSteps.current',
  later: 'setupSteps.later',
};

export interface SetupStepsProps {
  /** The screen's own name, which every step's data attributes carry: `data-<hook>-step` and its toggle and summary. */
  hook: string;
  children: ReactNode;
  /** Data attributes for the list itself, landed verbatim. */
  dataAttribute?: Record<string, string>;
  /**
   * The guide's own key (decision 2850). Given, the column stands under a heading row
   * with Hide Steps at its right, which folds the guide to that one row (the heading,
   * how many steps are done, Show Steps), remembered per device under this key. Absent,
   * the column draws as it always did.
   */
  guide?: string;
  /** The guide's heading, in the row that carries Hide Steps. */
  heading?: string;
  /** The heading's own class, where the screen's card styles it its own way. */
  headingClassName?: string | undefined;
  /** What stands between the heading and the steps while the guide is open. */
  intro?: ReactNode;
  /**
   * Whether a guide with every step done folds itself the first time it is seen done. Default true;
   * the Banking page folds its guide behind its own button once done (decision 1770) and passes false.
   */
  foldsWhenDone?: boolean;
}

/**
 * 🔨 `auto` is the guide's own fold, kept apart from `hidden`, which only a person's press writes (decision 2850; T2176): a step that
 * becomes undone again clears it, so the guide shows open and folds itself again the next time every step is done. `cleared`
 * reads as no choice at all.
 */
type GuideState = 'hidden' | 'shown' | 'auto';

const parseGuideState = (raw: string): GuideState | null => (raw === 'hidden' || raw === 'shown' || raw === 'auto' ? raw : null);

/** The steps a column holds and how many read done, counted off the children it was given. */
function countSteps(children: ReactNode): { done: number; total: number } {
  const steps = Children.toArray(children).filter((child): child is ReactElement<{ state?: SetupStepState }> => isValidElement(child) && typeof (child.props as { state?: unknown }).state === 'string');
  return { done: steps.filter((child) => child.props.state === 'done').length, total: steps.length };
}

/** The column the steps stand in, an ordered list, under its Hide Steps row where the screen names a guide. */
export function SetupSteps({ children, dataAttribute, guide, heading, headingClassName, intro, foldsWhenDone = true }: Omit<SetupStepsProps, 'hook'> & { hook?: string }) {
  const key = `${SETUP_STEPS_KEY_PREFIX}${guide ?? ''}`;
  const [stored, setStored] = useState<GuideState | null>(() => (guide ? readPreference<GuideState | null>(key, parseGuideState, null) : null));
  const { done, total } = countSteps(children);
  const allDone = total > 0 && done === total;
  // 🔒 A guide with every step done folds itself the first time it is seen done, and remembers that it did.
  const selfFolds = guide !== undefined && stored === null && allDone && foldsWhenDone;
  // 🔒 Its own fold stands only while every step is done: a step that comes undone again (a mailbox disconnects, a key is removed) clears the marker.
  const selfFolded = guide !== undefined && stored === 'auto' && allDone;
  const cleared = guide !== undefined && stored === 'auto' && !allDone;
  useEffect(() => {
    if (selfFolds) writePreference(key, 'auto');
    if (cleared) writePreference(key, 'cleared');
  }, [selfFolds, cleared, key]);
  const list = (
    <ol className={styles.steps} {...(dataAttribute ?? {})}>
      {children}
    </ol>
  );
  if (guide === undefined) return list;
  const folded = stored === 'hidden' || selfFolds || selfFolded;
  const press = () => {
    const next: GuideState = folded ? 'shown' : 'hidden';
    writePreference(key, next);
    setStored(next);
  };
  return (
    <div className={styles.guide} data-setup-guide={guide} data-setup-guide-folded={folded ? 'true' : 'false'}>
      <div className={styles.guideRow}>
        <h3 className={`${styles.guideHeading} ${headingClassName ?? ''}`}>{heading ?? t('setupSteps.heading')}</h3>
        {folded ? <span className={styles.guideCount} data-setup-guide-count={guide}>{fill(t('setupSteps.progress'), { done: String(done), total: String(total) })}</span> : null}
        <button type="button" className={styles.guideToggle} aria-expanded={folded ? 'false' : 'true'} data-setup-guide-toggle={guide} onClick={press}>
          {folded ? t('setupSteps.show') : t('setupSteps.hide')}
        </button>
      </div>
      {folded ? null : (
        <>
          {intro}
          {list}
        </>
      )}
    </div>
  );
}

export interface SetupStepProps {
  hook: string;
  number: number;
  /** The step's own name, which its data attributes carry. */
  name: string;
  title: string;
  state: SetupStepState;
  /** Whether a done step has been opened again; a current or later step is always open. */
  open: boolean;
  onToggle: () => void;
  summary: ReactNode;
  notice?: ReactNode;
  /**
   * What holds the step, one plain sentence (decision 1970): passed while the
   * step is later or its primary control is disabled, and the same words that
   * control's `disabledReason` carries. Blank or absent draws nothing, and a
   * done step never draws it.
   */
  holdReason?: string | null;
  children: ReactNode;
  /** Data attributes for the step, landed verbatim beside the composite's own. */
  dataAttribute?: Record<string, string>;
}

/**
 * One numbered step. A done step not opened again draws as one compact row
 * (decision 1850): the check, the title and Done on its first line, and the
 * summary in full under the title. The row drops the visible Step caption into words only a screen reader
 * reads, and pressing anywhere on the row opens the full
 * card it drew before.
 */
export function SetupStep({ hook, number, name, title, state, open, onToggle, summary, notice, holdReason, children, dataAttribute }: SetupStepProps) {
  const showBody = state !== 'done' || open;
  const compact = state === 'done' && !open;
  const numbered = `${t('setupSteps.step')} ${number}`;
  const badge = (
    <span className={styles.badge} aria-hidden="true">
      {state === 'done' ? <Icon meaning="confirmed" /> : number}
    </span>
  );
  const stateWord = <span className={styles.state}>{t(STATE_WORD[state])}</span>;
  const own = { [`data-${hook}-step`]: name };
  const toggle = { [`data-${hook}-step-toggle`]: name };
  const summaryHook = { [`data-${hook}-step-summary`]: name };
  // 🔒 The hold line (decision 1970): a done step is held by nothing, so it draws none.
  const hold = state !== 'done' && holdReason && holdReason.trim() !== '' ? (
    <p className={styles.hold} {...{ [`data-${hook}-step-hold`]: name }}>
      <Icon meaning="locked" />
      <span>{holdReason}</span>
    </p>
  ) : null;
  if (compact) {
    return (
      <li className={`${styles.step} ${styles.done} ${styles.compact}`} {...own} data-step-state={state} data-step-open="false" data-step-compact="true" {...(dataAttribute ?? {})}>
        <h3 className={styles.heading}>
          <button type="button" className={styles.header} aria-expanded="false" {...toggle} onClick={onToggle}>
            {badge}
            <span className={styles.title}>
              <span className={styles.srOnly}>{numbered}</span>{' '}
              {title}
            </span>
          </button>
        </h3>
        <div className={styles.summary} {...summaryHook}>{summary}</div>
        {stateWord}
        {notice}
      </li>
    );
  }
  const heading = (
    <>
      {badge}
      <span className={styles.title}>
        <span className={styles.number}>{numbered}</span>
        {title}
      </span>
      {stateWord}
    </>
  );
  return (
    <li className={`${styles.step} ${styles[state] ?? ''}`} {...own} data-step-state={state} data-step-open={showBody ? 'true' : 'false'} {...(dataAttribute ?? {})}>
      <h3 className={styles.heading}>
        {state === 'done' ? (
          <button type="button" className={styles.header} aria-expanded={open ? 'true' : 'false'} {...toggle} onClick={onToggle}>
            {heading}
          </button>
        ) : (
          <span className={styles.header}>{heading}</span>
        )}
      </h3>
      {hold}
      {notice}
      {showBody ? <div className={styles.body}>{children}</div> : null}
    </li>
  );
}
